import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { StatusBadge } from '../components/Badges';
import SafeIcon from '../common/SafeIcon';
import PartModal from '../components/Admin/PartModal';
import MissedSearches from '../components/Admin/MissedSearches';
import CSVImporter from '../components/Admin/CSVImporter';
import RejectionModal from '../components/Admin/RejectionModal';
import Notification from '../components/Admin/Notification';
import PartDetailPanel from '../components/PartDetailPanel';
import { getDisplayPN } from '../components/PartCard';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';

const { 
  FiPlus, FiEdit2, FiSearch, FiDatabase, FiUploadCloud, 
  FiShieldOff, FiClock, FiList, FiCheck, FiCreditCard, 
  FiUsers, FiCalendar, FiTarget, FiLoader, FiCheckCircle, 
  FiXCircle, FiUser, FiMessageSquare, FiTrendingUp, 
  FiDollarSign, FiActivity 
} = FiIcons;

const AdminDashboard = () => {
  const { parts, nlaTickets, refreshData, pendingNLACount } = useAppContext();
  const { profile, user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('inventory');
  const [queueTab, setQueueTab] = useState('Pending');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ type: null, data: null });
  const [selectedPart, setSelectedPart] = useState(null);
  const [notify, setNotify] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Stats and User Data
  const [subStats, setSubStats] = useState({ 
    total: 0, active: 0, monthly: 0, annual: 0, free: 0, mrr: 0, arr: 0 
  });
  const [allUsers, setAllUsers] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    const fetchAdminData = async () => {
      if (profile?.role !== 'admin') return;
      setLoadingStats(true);
      try {
        const { users: data } = await api('listUsers');

        if (data) {
          const monthlyCount = data.filter(u => u.subscription_plan === 'monthly' && (u.subscription_status === 'pro' || u.subscription_status === 'active')).length;
          const annualCount = data.filter(u => u.subscription_plan === 'annual' && (u.subscription_status === 'pro' || u.subscription_status === 'active')).length;
          
          setSubStats({
            total: data.length,
            active: data.filter(u => u.subscription_status === 'pro' || u.subscription_status === 'active').length,
            monthly: monthlyCount,
            annual: annualCount,
            free: data.filter(u => u.subscription_status === 'free').length,
            mrr: (monthlyCount * 29) + (annualCount * (279 / 12)),
            arr: (monthlyCount * 29 * 12) + (annualCount * 279)
          });
          setAllUsers(data);
        }
      } catch (err) {
        console.error('Admin Stats Error:', err);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchAdminData();
  }, [profile]);

  if (authLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <SafeIcon icon={FiLoader} className="w-10 h-10 text-blue-600 animate-spin" />
        <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Verifying Credentials...</p>
      </div>
    );
  }

  if (profile?.role !== 'admin') {
    return (
      <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 max-w-2xl mx-auto mt-10 shadow-sm">
        <SafeIcon icon={FiShieldOff} className="w-16 h-16 text-slate-400 dark:text-slate-700 mx-auto mb-6" />
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3">Admin Access Restricted</h2>
        <p className="text-slate-500 dark:text-slate-400 px-10">Authorized personnel only. Your account does not have administrative permissions.</p>
      </div>
    );
  }

  const handleApprove = async (ticket) => {
    setIsProcessing(true);
    try {
      // The server records the reviewer from the session and marks the part Discontinued
      await api('reviewTicket', { id: ticket.id, decision: 'Approved' });

      setNotify({ message: 'Part successfully marked as NLA', type: 'success' });
      refreshData();
    } catch (err) {
      setNotify({ message: err.message, type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async (reason) => {
    const ticket = modal.data;
    setIsProcessing(true);
    try {
      await api('reviewTicket', { id: ticket.id, decision: 'Rejected', notes: reason });
      setNotify({ message: 'NLA flag rejected', type: 'success' });
      setModal({ type: null, data: null });
      refreshData();
    } catch (err) {
      setNotify({ message: err.message, type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const getPlanLabel = (u) => {
    if (u.subscription_status === 'free') return 'Free Tier';
    if (u.subscription_plan === 'monthly') return 'Monthly';
    if (u.subscription_plan === 'annual') return 'Annual';
    return 'Free Tier';
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      pro: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      free: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      canceled: 'bg-red-500/10 text-red-500 border-red-500/20',
      past_due: 'bg-amber-500/10 text-amber-500 border-amber-500/20'
    };
    const labels = {
      active: 'Active',
      pro: 'Active',
      free: 'Free',
      canceled: 'Canceled',
      past_due: 'Past Due'
    };
    const currentStyle = styles[status] || styles.free;
    const currentLabel = labels[status] || 'Free';
    
    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg border text-[10px] font-black uppercase ${currentStyle}`}>
        <div className={`w-1 h-1 rounded-full ${status === 'active' || status === 'pro' ? 'bg-emerald-500 animate-pulse' : 'bg-current opacity-50'}`} />
        {currentLabel}
      </div>
    );
  };

  const filteredQueue = nlaTickets.filter(t => t.status === queueTab);

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 text-left">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-600/10 p-2 rounded-lg border border-blue-500/20">
              <SafeIcon icon={FiDatabase} className="text-blue-600 dark:text-blue-500 w-5 h-5" />
            </div>
            <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">Control Center</h1>
          </div>
          <p className="text-slate-500 dark:text-slate-400 font-medium tracking-tight">Managing global intelligence and revenue streams.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setModal({ type: 'csv' })} className="flex items-center gap-2 px-6 py-3 rounded-xl font-black text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50">
            <SafeIcon icon={FiUploadCloud} /> Bulk CSV Load
          </button>
          <button onClick={() => setModal({ type: 'part', data: null })} className="flex items-center gap-2 px-6 py-3 rounded-xl font-black text-sm bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-900/20">
            <SafeIcon icon={FiPlus} /> Manual Entry
          </button>
        </div>
      </div>

      <div className="flex gap-1 bg-slate-100 dark:bg-slate-900 p-1.5 rounded-2xl w-fit border border-slate-200 dark:border-slate-800">
        <TabButton active={activeTab === 'inventory'} onClick={() => setActiveTab('inventory')} icon={FiList} label="Inventory" />
        <TabButton active={activeTab === 'nla_queue'} onClick={() => setActiveTab('nla_queue')} icon={FiClock} label="NLA Queue" badge={pendingNLACount} />
        <TabButton active={activeTab === 'missed'} onClick={() => setActiveTab('missed')} icon={FiSearch} label="Missed Searches" />
        <TabButton active={activeTab === 'subscriptions'} onClick={() => setActiveTab('subscriptions')} icon={FiCreditCard} label="Revenue" />
      </div>

      {activeTab === 'inventory' && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden shadow-sm">
          <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[300px] text-left">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-900 dark:text-white outline-none focus:border-blue-500 transition-all" placeholder="Global PN Search..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="overflow-x-auto text-left">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-950/50 text-slate-500 text-[10px] font-black uppercase tracking-widest border-b border-slate-200 dark:border-slate-800/50">
                <tr>
                  <th className="px-8 py-5 text-left">OEM PN</th>
                  <th className="px-8 py-5 text-left">Internal PN</th>
                  <th className="px-8 py-5 text-left">Description</th>
                  <th className="px-8 py-5 text-left">Status</th>
                  <th className="px-8 py-5 text-right">Ops</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                {parts.map(p => (
                  <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors group cursor-pointer" onClick={() => setSelectedPart(p)}>
                    <td className="px-8 py-5 text-left">
                      <div className="font-mono text-slate-900 dark:text-white font-black text-base group-hover:text-blue-600">{getDisplayPN(p)}</div>
                    </td>
                    <td className="px-8 py-5 text-left">
                      <div className="font-mono text-slate-500 text-sm">{p.part_numbr || '---'}</div>
                    </td>
                    <td className="px-8 py-5 text-left">
                      <div className="text-sm text-slate-700 dark:text-slate-300 font-bold truncate max-w-[300px]">{p.descrip1}</div>
                    </td>
                    <td className="px-8 py-5 text-left">
                      <StatusBadge status={p.status || 'Active'} />
                    </td>
                    <td className="px-8 py-5 text-right space-x-2">
                      <button onClick={(e) => { e.stopPropagation(); setModal({ type: 'part', data: p }); }} className="p-2.5 text-slate-400 hover:text-blue-600 bg-slate-100 dark:bg-slate-950/50 rounded-xl">
                        <SafeIcon icon={FiEdit2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'missed' && (
        <MissedSearches onAddPart={(query) => setModal({ type: 'part', data: null, prefill: { oem_pn: query } })} />
      )}

      {activeTab === 'nla_queue' && (
        <div className="space-y-6">
          <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
            {['Pending', 'Approved', 'Rejected'].map(status => (
              <button key={status} onClick={() => setQueueTab(status)} className={`pb-4 px-2 text-xs font-black uppercase tracking-widest transition-all relative ${queueTab === status ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`} >
                {status} {queueTab === status && <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 rounded-t-full" />}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredQueue.map(ticket => (
              <div key={ticket.id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all text-left">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-black text-blue-600 dark:text-blue-500 uppercase tracking-widest bg-blue-50 dark:bg-blue-500/10 px-2 py-0.5 rounded">
                        {ticket.parts?.appl_brand || 'Unknown Brand'}
                      </span>
                      <span className="text-[10px] font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                        {ticket.parts?.equip_type}
                      </span>
                    </div>
                    <h3 className="text-xl font-black text-slate-900 dark:text-white leading-tight">{ticket.parts?.descrip1}</h3>
                    <div className="text-sm font-mono font-bold text-slate-500 mt-1">OEM:{getDisplayPN(ticket.parts)} / Int:{ticket.parts?.part_numbr}</div>
                  </div>
                  {ticket.status !== 'Pending' && (
                    <div className={`p-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800`}>
                      <SafeIcon icon={ticket.status === 'Approved' ? FiCheckCircle : FiXCircle} className={ticket.status === 'Approved' ? 'text-emerald-500' : 'text-red-500'} />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 py-4 border-y border-slate-100 dark:border-slate-800/50 mb-4">
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Submitted By</div>
                    <div className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{ticket.submitted_by_user?.full_name || ticket.submitted_by_user?.email || 'System'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Date</div>
                    <div className="text-xs font-bold text-slate-800 dark:text-slate-200">{format(new Date(ticket.submitted_at), 'MMM dd, yyyy')}</div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-slate-50 dark:bg-slate-950 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                      <SafeIcon icon={FiMessageSquare} className="text-blue-500" /> Tech Notes
                    </div>
                    <p className="text-xs font-medium text-slate-600 dark:text-slate-400 leading-relaxed italic">
                      "{ticket.tech_notes || 'No tech notes provided'}"
                    </p>
                  </div>
                  {ticket.status === 'Pending' && (
                    <div className="flex gap-3 pt-2">
                      <button onClick={() => setModal({ type: 'reject', data: ticket })} className="flex-1 py-3 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 font-black rounded-xl text-[10px] uppercase tracking-widest transition-all">
                        Reject Flag
                      </button>
                      <button onClick={() => handleApprove(ticket)} disabled={isProcessing} className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-black rounded-xl text-[10px] uppercase tracking-widest transition-all shadow-lg shadow-emerald-900/20 flex items-center justify-center gap-2">
                        {isProcessing ? 'Processing...' : <><SafeIcon icon={FiCheck} /> Approve NLA</>}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'subscriptions' && (
        <div className="space-y-8 text-left animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <StatCard label="MRR" val={`$${Math.round(subStats.mrr).toLocaleString()}`} icon={FiTrendingUp} color="emerald" isCurrency />
            <StatCard label="ARR" val={`$${Math.round(subStats.arr).toLocaleString()}`} icon={FiDollarSign} color="blue" isCurrency />
            <StatCard label="Total Users" val={subStats.total} icon={FiUsers} color="slate" />
            <StatCard label="Active Pro" val={subStats.active} icon={FiCheckCircle} color="emerald" />
            <StatCard label="Free Users" val={subStats.free} icon={FiUser} color="blue" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 shadow-sm">
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                  <SafeIcon icon={FiActivity} className="text-blue-500" /> Plan Distribution
                </h3>
                <div className="space-y-6">
                  <PlanRow label="Monthly ($29)" count={subStats.monthly} total={subStats.active} color="bg-blue-500" />
                  <PlanRow label="Annual ($279)" count={subStats.annual} total={subStats.active} color="bg-emerald-500" />
                  <PlanRow label="Free Tier" count={subStats.free} total={subStats.total} color="bg-slate-400" />
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex justify-between items-center">
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <SafeIcon icon={FiUsers} className="text-blue-500" /> All Users
                  </h3>
                  {loadingStats && <FiLoader className="w-4 h-4 animate-spin text-blue-500" />}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-950/50 text-[10px] font-black uppercase text-slate-400 tracking-widest border-b border-slate-100 dark:border-slate-800">
                      <tr>
                        <th className="px-6 py-4 text-left">Technician</th>
                        <th className="px-6 py-4 text-left">Plan</th>
                        <th className="px-6 py-4 text-left">Join Date</th>
                        <th className="px-6 py-4 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                      {allUsers.map(u => (
                        <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                          <td className="px-6 py-4">
                            <div className="text-sm font-bold text-slate-900 dark:text-white">{u.full_name || 'Unnamed Tech'}</div>
                            <div className="text-[10px] text-slate-500 font-medium">{u.email}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-[10px] font-black uppercase tracking-tight text-slate-600 dark:text-slate-400">
                              {getPlanLabel(u)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-xs font-mono font-bold text-slate-500">
                            {u.created_at ? format(new Date(u.created_at), 'MMM dd, yyyy') : '---'}
                          </td>
                          <td className="px-6 py-4 text-right">
                            {getStatusBadge(u.subscription_status)}
                          </td>
                        </tr>
                      ))}
                      {allUsers.length === 0 && !loadingStats && (
                        <tr>
                          <td colSpan="4" className="px-6 py-12 text-center text-slate-500 italic text-sm">
                            No registered users found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedPart && <PartDetailPanel part={selectedPart} onClose={() => setSelectedPart(null)} />}
      {modal.type === 'csv' && <CSVImporter existingParts={parts} onClose={() => setModal({ type: null })} onSuccess={(m) => { setNotify({ message: m, type: 'success' }); refreshData(); }} />}
      {modal.type === 'part' && <PartModal part={modal.data} prefill={modal.prefill} onClose={() => setModal({ type: null, data: null })} onSuccess={(m) => { setNotify({ message: m, type: 'success' }); refreshData(); }} />}
      {modal.type === 'reject' && <RejectionModal ticket={modal.data} onClose={() => setModal({ type: null, data: null })} onConfirm={handleReject} />}
      {notify && <Notification {...notify} onClose={() => setNotify(null)} />}
    </div>
  );
};

const TabButton = ({ active, onClick, icon, label, badge }) => (
  <button onClick={onClick} className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 ${active ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
    <SafeIcon icon={icon} /> {label} {badge > 0 && <span className="ml-1 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{badge}</span>}
  </button>
);

const StatCard = ({ label, val, icon, color, isCurrency }) => {
  const colors = {
    blue: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 border-blue-100 dark:border-blue-500/20',
    emerald: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 border-emerald-100 dark:border-emerald-500/20',
    amber: 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 border-amber-100 dark:border-amber-500/20',
    slate: 'bg-slate-50 dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-800'
  };
  return (
    <div className={`p-4 rounded-2xl border ${colors[color]} flex flex-col justify-between shadow-sm`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-2 truncate">{label}</div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xl font-black truncate">{val}</span>
        <SafeIcon icon={icon} className="w-4 h-4 opacity-30 shrink-0" />
      </div>
    </div>
  );
};

const PlanRow = ({ label, count, total, color }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-[10px] font-black uppercase tracking-wider">
        <span className="text-slate-500">{label}</span>
        <span className="text-slate-900 dark:text-white">{count} ({Math.round(percentage)}%)</span>
      </div>
      <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-1000`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

export default AdminDashboard;